import React from "react";
import { Button } from "@mui/material";

/**
 * ExportToExcel
 * Props:
 *  - data: array of order objects (already filtered/sorted)
 *  - clientsMap, shippingMap, governoratesMap, citiesMap: lookup maps for readable names
 *  - filename: optional output filename (default: orders_export.csv)
 */
const ExportToExcel = ({ data = [], clientsMap = {}, shippingMap = {}, governoratesMap = {}, citiesMap = {}, filename = "orders_export.csv" }) => {
  const escapeCell = (v) => {
    if (v == null) return '""';
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return `"${String(s).replace(/"/g, '""')}"`;
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(); // localized display (12/24 depends on environment)
  };
  const exportCSV = () => {
    if (!Array.isArray(data) || !data.length) return;

    // Arabic headers and key order
    const headers = [
      { key: "order_number", label: "رقم الطلب" },
      { key: "created_at_12h", label: "تاريخ الإنشاء (12 س)" },
      { key: "status", label: "الحالة" },
      { key: "payment_method", label: "طريقة الدفع" },
      { key: "total", label: "الإجمالي" },
      { key: "subtotal", label: "المجموع الفرعي" },
      { key: "shipping", label: "تكلفة الشحن" },
      { key: "discount", label: "الخصم" },
      { key: "checkout_phone", label: "هاتف الطلب (checkout)" },
      { key: "client_name", label: "اسم العميل" },
      { key: "client_phone", label: "هاتف العميل" },
      { key: "client_email", label: "بريد العميل" },
      { key: "country", label: "البلد" },
      { key: "governorate", label: "المحافظة" },
      { key: "city", label: "المدينة" },
      { key: "address", label: "العنوان" },
      { key: "postal_code", label: "الرمز البريدي" },
      { key: "notes", label: "ملاحظات" },
      { key: "items", label: "العناصر (items)" },
      { key: "payments", label: "المدفوعات (payments)" },
    ];

    const rows = data.map((o) => {
      const co = o.check_out || {};
      const uid = o.user_id ?? o.user?.id;
      const client = clientsMap?.[uid] || {};
      const country = shippingMap?.[co.country_id] ?? co.country_id ?? "";
      const governorate = governoratesMap?.[co.governorate_id]?.name ?? co.governorate_id ?? "";
      const city = citiesMap?.[co.city_id]?.name ?? co.city_id ?? "";
      const items = o.items ? o.items.map((it) => ({
        id: it.id,
        product_id: it.product?.id ?? it.product_id,
        product_name: it.product?.name_ar || it.product?.name_en || it.product?.name || "",
        qty: it.quantity,
        price: it.price,
        total: it.total,
      })) : [];
      const payments = Array.isArray(o.payments) ? o.payments.map((p) => ({
        id: p.id,
        method: p.payment_method,
        amount: p.amount,
        status: p.status,
      })) : [];

      return {
        order_number: o.order_number ?? "",
        created_at_12h: o.created_at ? formatDate(o.created_at) : "",
        status: o.status ?? "",
        payment_method: o.payment_method ?? "",
        total: o.total ?? "",
        subtotal: co.subtotal ?? "",
        shipping: co.shipping_cost ?? co.shipping ?? "",
        discount: co.discount ?? "",
        checkout_phone: co.phone ?? "",
        client_name: client.name || client.full_name || client.email || "",
        client_phone: client.phone || "",
        client_email: client.email || "",
        country,
        governorate,
        city,
        address: co.address || "",
        postal_code: co.postal_code || "",
        notes: co.notes || "",
        items,
        payments,
      };
    });

    // Build CSV content with Arabic header row
    const headerRow = headers.map((h) => escapeCell(h.label)).join(",");
    const dataRows = rows.map((r) =>
      headers.map((h) => {
        const v = r[h.key];
        // For arrays/objects (items/payments/raw_json), stringify with readable JSON
        if (Array.isArray(v) || typeof v === "object") {
          return escapeCell(JSON.stringify(v, null, 0));
        }
        return escapeCell(v);
      }).join(",")
    );

    const csvContent = "\uFEFF" + [headerRow, ...dataRows].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Button variant="outlined" onClick={exportCSV} size="small" disabled={!data || !data.length}>
      تصدير Excel 
    </Button>
  );
};

export default ExportToExcel;
